import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import translations from 'services/translations/translations';
import CheckboxInputField from 'components/layout/forms/CheckboxInputField';
import NumberInputField from 'components/layout/forms/NumberInputField';
import IWall from 'components/interfaces/IWall';
import { useDispatch } from 'react-redux';
import handleGlassWallProperties, {
  handleGlassWallPropertiesForType,
} from './handleGlassWallProperties';
import {
  woodyWallProfileHeightOptions,
  woodyQuantityDecorlistOptions,
} from '../../../types/woodyWallOptions';
import { setWall } from '../../../../../store/selectedWall/actions';
import store from '../../../../../store/store';
import getFieldErrorMessage from './getFieldErrorMessage';
import CreatableDropdown from '../../../../layout/forms/CreatableDropdown';
import showOptions from '../../../../layout/forms/showOptions';
import standingWoodyProfileHeights from '../../../types/standingWoodyProfileHeights';
import { CREATABLE_DROPDOWN_EVENT } from '../../../../../services/objects/editFieldEventHandler';
import IValidationError from './IValidationError';
import WoodyMasterField, { WoodyVariant } from './WoodyMasterField';

const WoodyWallProperties = ({
  wall,
  disableInputs,
  validationErrors,
}: {
  wall: IWall;
  disableInputs?: boolean;
  validationErrors: IValidationError[];
}): JSX.Element | null => {
  const { t } = useTranslation([translations.glassWallProperties]);
  const dispatch = useDispatch();

  const handleWallIntegerProperty = (event: any, propertyName: string) => {
    // Read the latest wall from the store rather than the captured prop so
    // that broadcasting a master change across several variants accumulates
    // each update instead of overwriting it with a stale copy.
    const currentWall = store.getState().selectedWall!.properties as IWall;
    dispatch(
      setWall({
        ...currentWall,
        [propertyName]: parseInt(event.target.value, 10),
      })
    );
  };

  const handleWallSelectChange = (event: any) =>
    handleWallIntegerProperty(event, event.target.name);

  const makeVariant = (name: keyof IWall, labelKey: string): WoodyVariant => ({
    name,
    label: t(`${translations.woodyWallProperties}:fields:${labelKey}`),
    value: wall[name] as number | boolean,
    error: getFieldErrorMessage(name, validationErrors),
  });

  return (
    <div>
      <Row>
        <Col>
          <WoodyMasterField
            masterLabel={t(
              `${translations.woodyWallProperties}:fields:profileHeight`
            )}
            type="select"
            options={woodyWallProfileHeightOptions()}
            onChange={handleWallSelectChange}
            disabled={disableInputs}
            variants={[
              makeVariant('woodyLeftProfileHeight', 'leftProfileHeight'),
              makeVariant('woodyRightProfileHeight', 'rightProfileHeight'),
              makeVariant('woodyFloorProfileHeight', 'floorProfileHeight'),
              makeVariant('woodyRoofProfileHeight', 'roofProfileHeight'),
            ]}
          />
        </Col>
        <Col>
          <WoodyMasterField
            masterLabel={t(
              `${translations.woodyWallProperties}:fields:glassDepth`
            )}
            type="number"
            onChange={handleGlassWallProperties}
            disabled={disableInputs}
            variants={[
              makeVariant('woodyLeftGlassDepth', 'leftGlassDepth'),
              makeVariant('woodyRightGlassDepth', 'rightGlassDepth'),
              makeVariant('woodyFloorGlassDepth', 'floorGlassDepth'),
              makeVariant('woodyRoofGlassDepth', 'roofGlassDepth'),
            ]}
          />
        </Col>
        <Col>
          <WoodyMasterField
            masterLabel={t(
              `${translations.woodyWallProperties}:fields:grooveDepth`
            )}
            type="number"
            onChange={handleGlassWallProperties}
            disabled={disableInputs}
            variants={[
              makeVariant('woodyLeftGrooveDepth', 'leftGrooveDepth'),
              makeVariant('woodyRightGrooveDepth', 'rightGrooveDepth'),
              makeVariant('woodyFloorGrooveDepth', 'floorGrooveDepth'),
              makeVariant('woodyRoofGrooveDepth', 'roofGrooveDepth'),
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <WoodyMasterField
            masterLabel={t(
              `${translations.woodyWallProperties}:fields:grooveWidth`
            )}
            type="number"
            onChange={handleGlassWallProperties}
            disabled={disableInputs}
            variants={[
              makeVariant('woodyLeftGrooveWidth', 'leftGrooveWidth'),
              makeVariant('woodyRightGrooveWidth', 'rightGrooveWidth'),
              makeVariant('woodyFloorGrooveWidth', 'floorGrooveWidth'),
              makeVariant('woodyRoofGrooveWidth', 'roofGrooveWidth'),
            ]}
          />
        </Col>
        <Col>
          <NumberInputField
            error={getFieldErrorMessage('woodyProfileWidth', validationErrors)}
            id="woodyProfileWidth"
            label={t(`${translations.woodyWallProperties}:fields:profileWidth`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyProfileWidth}
            name="woodyProfileWidth"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyGlassOffset"
            label={t(`${translations.woodyWallProperties}:fields:glassOffset`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyGlassOffset}
            name="woodyGlassOffset"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            id="woodyRoofProfileOffset"
            label={t(
              `${translations.woodyWallProperties}:fields:roofProfileOffset`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyRoofProfileOffset}
            name="woodyRoofProfileOffset"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyFloorProfileOffset"
            label={t(
              `${translations.woodyWallProperties}:fields:floorProfileOffset`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyFloorProfileOffset}
            name="woodyFloorProfileOffset"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <WoodyMasterField
            masterLabel={t(
              `${translations.woodyWallProperties}:fields:glassGroove`
            )}
            type="checkbox"
            onChange={handleGlassWallProperties}
            variants={[
              makeVariant('woodyLeftGlassGroove', 'leftProfileGlassGrove'),
              makeVariant('woodyRightGlassGroove', 'rightProfileGlassGrove'),
              makeVariant('woodyFloorGlassGroove', 'floorProfileGlassGrove'),
              makeVariant('woodyRoofGlassGroove', 'roofProfileGlassGrove'),
            ]}
          />
        </Col>
        <Col>
          <WoodyMasterField
            masterLabel={t(
              `${translations.woodyWallProperties}:fields:guideGroove`
            )}
            type="checkbox"
            onChange={handleGlassWallProperties}
            variants={[
              makeVariant('woodyLeftGuideGroove', 'leftGuideGroove'),
              makeVariant('woodyRightGuideGroove', 'rightGuideGroove'),
              makeVariant('woodyFloorGuideGroove', 'floorGuideGroove'),
              makeVariant('woodyRoofGuideGroove', 'roofGuideGroove'),
            ]}
          />
        </Col>
        <Col>
          <WoodyMasterField
            masterLabel={t(
              `${translations.woodyWallProperties}:fields:backGroove`
            )}
            type="checkbox"
            onChange={handleGlassWallProperties}
            variants={[
              makeVariant('woodyLeftBackGroove', 'leftBackGroove'),
              makeVariant('woodyRightBackGroove', 'rightBackGroove'),
              makeVariant('woodyFloorBackGroove', 'floorBackGroove'),
              makeVariant('woodyRoofBackGroove', 'roofBackGroove'),
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="woodyQuantityDecorList"
            label={t(`${translations.woodyWallProperties}:fields:decorListQty`)}
            name="woodyQuantityDecorList"
            onChange={(e: any) =>
              handleWallIntegerProperty(e, 'woodyQuantityDecorList')
            }
            options={woodyQuantityDecorlistOptions()}
            required
            value={wall.woodyQuantityDecorList}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyHeightDecorList"
            label={t(
              `${translations.woodyWallProperties}:fields:woodyHeightDecor`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyHeightDecorList}
            name="woodyHeightDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyThicknessOutsideDecorList"
            label={t(`${translations.woodyWallProperties}:fields:tOutside`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyThicknessOutsideDecorList}
            name="woodyThicknessOutsideDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodyThicknessInsideDecorList"
            label={t(`${translations.woodyWallProperties}:fields:tInside`)}
            onChange={handleGlassWallProperties}
            value={wall.woodyThicknessInsideDecorList}
            name="woodyThicknessInsideDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="woodySingleHeightDecorList"
            label={t(`${translations.woodyWallProperties}:fields:singleHeight`)}
            onChange={handleGlassWallProperties}
            value={wall.woodySingleHeightDecorList}
            name="woodySingleHeightDecorList"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="woodyIncludeWoodenLists"
            label={t(
              `${translations.woodyWallProperties}:fields:includeWoodenLists`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyIncludeWoodenLists}
            name="woodyIncludeWoodenLists"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="woodyIncludeWoodyLeftProfile"
            label={t(
              `${translations.woodyWallProperties}:fields:includeWoodyWallLeftProfile`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyIncludeWoodyLeftProfile}
            name="woodyIncludeWoodyLeftProfile"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="woodyIncludeWoodyRightProfile"
            label={t(
              `${translations.woodyWallProperties}:fields:includeWoodyWallRightProfile`
            )}
            onChange={handleGlassWallProperties}
            value={wall.woodyIncludeWoodyRightProfile}
            name="woodyIncludeWoodyRightProfile"
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CreatableDropdown
            value={{
              label: wall.leftStandingWoodyProfileHeight,
              value: wall.leftStandingWoodyProfileHeight,
            }}
            name="leftStandingWoodyProfileHeight"
            onChange={(e: any) => {
              handleGlassWallPropertiesForType(
                e,
                CREATABLE_DROPDOWN_EVENT,
                'leftStandingWoodyProfileHeight'
              );
            }}
            options={showOptions(
              standingWoodyProfileHeights,
              translations.common,
              t
            )}
            placeholder=""
            label={t(
              `${translations.woodyWallProperties}:fields:leftStandingWoodyProfileHeight`
            )}
            disabled={false}
          />
        </Col>
        <Col>
          <CreatableDropdown
            value={{
              label: wall.rightStandingWoodyProfileHeight,
              value: wall.rightStandingWoodyProfileHeight,
            }}
            name="rightStandingWoodyProfileHeight"
            onChange={(e: any) => {
              handleGlassWallPropertiesForType(
                e,
                CREATABLE_DROPDOWN_EVENT,
                'rightStandingWoodyProfileHeight'
              );
            }}
            options={showOptions(
              standingWoodyProfileHeights,
              translations.common,
              t
            )}
            placeholder=""
            label={t(
              `${translations.woodyWallProperties}:fields:rightStandingWoodyProfileHeight`
            )}
            disabled={false}
          />
        </Col>
      </Row>
    </div>
  );
};

export default WoodyWallProperties;
